import type { PropsWithChildren, ReactNode } from "react";

import { useFragment } from "react-relay";
import { useLocation } from "react-router";
import { graphql } from "relay-runtime";

import { PageWithBreadCrumbs } from "app/components/layout/PageWithBreadCrumbs";
import {
  getAdminSectionCrumb,
  type BreadcrumbLink,
} from "app/constants/breadcrumbs";

import type { AdminPage_query$key } from "./__generated__/AdminPage_query.graphql";

export interface AdminPageProps extends PropsWithChildren {
  queryRef: AdminPage_query$key;
  isForm?: boolean;
  title?: string;
  withPaper?: boolean;
  /** Whether the layout renders `title` as the page heading (H2). Disable for pages that render their own header. */
  withHeader?: boolean;
  /** Crumbs between the section root and the current page (e.g. a tab view or parent entity). */
  parentCrumbs?: BreadcrumbLink[];
  /** Help shown in the side panel while `?help=true`. */
  helpContent?: ReactNode;
}

export function AdminPage({
  children,
  title = "",
  isForm = false,
  withPaper = true,
  withHeader = true,
  queryRef,
  parentCrumbs,
  helpContent,
}: AdminPageProps) {
  const data = useFragment(
    graphql`
      fragment AdminPage_query on Query {
        ...PageWithBreadCrumbs_query
      }
    `,
    queryRef,
  );

  const { pathname } = useLocation();

  // Trails root at the sidenav item owning the URL (Accounts/Bindings/
  // Policies); pages served outside /admin/* supply their root via
  // parentCrumbs instead.
  const sectionCrumb = getAdminSectionCrumb(pathname);
  const breadcrumbs = [
    ...(sectionCrumb ? [sectionCrumb] : []),
    ...(parentCrumbs ?? []),
  ];

  const sectionLabel = sectionCrumb?.label ?? parentCrumbs?.[0]?.label;
  // Falls back to the section when no explicit title is given, so the
  // breadcrumb and browser title (`Accounts | …`) stay meaningful on list pages.
  const documentTitle = title || sectionLabel || "Admin";

  return (
    <PageWithBreadCrumbs
      breadcrumbs={breadcrumbs}
      defaultTitle={sectionLabel}
      heading={withHeader ? title : undefined}
      helpContent={helpContent}
      isForm={isForm}
      queryRef={data}
      title={documentTitle}
      withPaper={withPaper}
    >
      {children}
    </PageWithBreadCrumbs>
  );
}
